import { useEffect, useRef, useState } from "react";
import jsQR from "jsqr";

const useQRScanner = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const animationFrameRef = useRef<number | undefined>(undefined);
  const lastQrCodeRef = useRef<string>("");

  // Lepsze wykrywanie urządzeń iOS
  const isIOS = () => {
    return (
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
    );
  };

  const isMobile = () => {
    return /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  };

  // Sprawdź czy jest HTTPS (wymagane na iOS)
  const isHTTPS = () => {
    return window.location.protocol === "https:" || window.location.hostname === "localhost";
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsVisible(entry.isIntersecting);
        });
      },
      { threshold: 0.1 }
    );

    if (videoRef.current) {
      observer.observe(videoRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const startCamera = async () => {
      if (!isVisible) {
        if (videoRef.current && videoRef.current.srcObject) {
          const stream = videoRef.current.srcObject as MediaStream;
          stream.getTracks().forEach((track) => track.stop());
          videoRef.current.srcObject = null;
        }
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current);
        }
        return;
      }

      // Sprawdź HTTPS na iOS
      if (isIOS() && !isHTTPS()) {
        setError("Aplikacja wymaga HTTPS do działania na iOS. Użyj localhost lub włącz HTTPS.");
        return;
      }

      const tryGetStream = async (constraints: MediaStreamConstraints) => {
        try {
          const stream = await navigator.mediaDevices.getUserMedia(constraints);
          const videoTrack = stream.getVideoTracks()[0];

          // iOS-specific: sprawdź czy track ma capabilities
          if (videoTrack.getCapabilities) {
            const capabilities = videoTrack.getCapabilities();
            if ("focusMode" in capabilities) {
              try {
                await videoTrack.applyConstraints({
                  advanced: [{ focusMode: "continuous" }] as any,
                });
              } catch (error) {
                console.warn("Focus mode not supported", error);
              }
            }
          }

          return stream;
        } catch (error) {
          console.warn("Camera access failed with constraints:", constraints, error);
          return null;
        }
      };

      let stream = null;
      const iosDevice = isIOS();
      const mobileDevice = isMobile();

      // iOS-specific constraints - różne opcje dla różnych wersji iOS
      const constraintsBack = {
        video: {
          facingMode: { exact: "environment" },
          width: { ideal: 640 },
          height: { ideal: 480 },
        },
      };
      const constraintsFront = {
        video: {
          facingMode: "user",
          width: { ideal: 640 },
          height: { ideal: 480 },
        },
      };

      // iOS Safari może wymagać prostszych constraints
      const constraintsBackSimple = {
        video: {
          facingMode: "environment",
        },
      };
      const constraintsFrontSimple = {
        video: {
          facingMode: "user",
        },
      };

      // Najprostsze constraints dla problematycznych urządzeń iOS
      const constraintsBasic = {
        video: true,
      };

      if (iosDevice) {
        // iOS: spróbuj różne opcje constraints w kolejności
        stream =
          (await tryGetStream(constraintsBackSimple)) ||
          (await tryGetStream(constraintsFrontSimple)) ||
          (await tryGetStream(constraintsBack)) ||
          (await tryGetStream(constraintsFront)) ||
          (await tryGetStream(constraintsBasic));
      } else if (mobileDevice) {
        stream = (await tryGetStream(constraintsBack)) || (await tryGetStream(constraintsFront));
      } else {
        stream = (await tryGetStream(constraintsFront)) || (await tryGetStream(constraintsBack));
      }

      if (stream && videoRef.current) {
        videoRef.current.srcObject = stream;

        // iOS-specific: obsługa autoplay z lepszym error handling
        const playVideo = async () => {
          try {
            if (videoRef.current) {
              // iOS Safari może wymagać muted dla autoplay
              videoRef.current.muted = true;
              videoRef.current.playsInline = true;
              await videoRef.current.play();
              setError(null);
            }
          } catch (error) {
            console.warn("Autoplay failed, trying with user interaction:", error);
            setError("Kliknij na wideo, aby uruchomić kamerę");
          }
        };

        videoRef.current.onloadedmetadata = () => {
          playVideo();
        };

        // iOS: dodaj event listener dla user interaction
        const handleUserInteraction = async () => {
          if (videoRef.current && videoRef.current.paused) {
            try {
              await videoRef.current.play();
              setError(null);
            } catch (error) {
              console.error("Failed to play video after user interaction:", error);
              setError("Nie można uruchomić kamery. Sprawdź uprawnienia.");
            }
          }
        };

        videoRef.current.addEventListener("click", handleUserInteraction);
        videoRef.current.addEventListener("touchstart", handleUserInteraction);

        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");

        const scan = () => {
          if (!isVisible) return;

          if (
            videoRef.current &&
            videoRef.current.readyState === videoRef.current.HAVE_ENOUGH_DATA &&
            videoRef.current.videoWidth > 0 &&
            videoRef.current.videoHeight > 0
          ) {
            canvas.width = videoRef.current.videoWidth;
            canvas.height = videoRef.current.videoHeight;
            context?.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);

            const imageData = context?.getImageData(0, 0, canvas.width, canvas.height);
            if (imageData) {
              const code = jsQR(imageData.data, imageData.width, imageData.height);
              if (code && code.data !== lastQrCodeRef.current) {
                lastQrCodeRef.current = code.data;
                setQrCode(code.data);
              }
            }
          }
          // Zmniejsz częstotliwość skanowania do 15 FPS
          setTimeout(() => {
            animationFrameRef.current = requestAnimationFrame(scan);
          }, 66); // ~15 FPS
        };

        scan();

        // Cleanup function
        return () => {
          videoRef.current?.removeEventListener("click", handleUserInteraction);
          videoRef.current?.removeEventListener("touchstart", handleUserInteraction);
        };
      } else {
        console.error("Unable to access any camera.");
        if (iosDevice) {
          setError("Nie można uzyskać dostępu do kamery na iOS. Sprawdź uprawnienia w Ustawieniach > Safari > Kamera.");
        } else {
          setError("Nie można uzyskać dostępu do kamery. Sprawdź uprawnienia.");
        }
      }
    };

    startCamera();

    return () => {
      if (videoRef.current && videoRef.current.srcObject) {
        const stream = videoRef.current.srcObject as MediaStream;
        stream.getTracks().forEach((track) => track.stop());
        videoRef.current.srcObject = null;
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [isVisible]);

  return { videoRef, qrCode, setQrCode, error };
};

export default useQRScanner;
